import { trigger, transition, style, animate } from '@angular/animations';
import * as i0 from '@angular/core';
import { InjectionToken, inject, Component, ChangeDetectionStrategy, HostBinding, Input, ElementRef, ComponentFactoryResolver, ApplicationRef, Injector, Directive, Injectable, NgModule } from '@angular/core';
import * as i1 from '@simpl/element-ng/translate';
import { SiTranslateModule } from '@simpl/element-ng/translate';
import { ComponentPortal, DomPortalOutlet } from '@angular/cdk/portal';
import { BehaviorSubject, merge, timer, combineLatest } from 'rxjs';
import { filter, switchMap, map, takeUntil } from 'rxjs/operators';
import { CommonModule } from '@angular/common';

const LOADING_SPINNER_BLOCKING = new InjectionToken('isBlockingSpinner');
const LOADING_SPINNER_OVERLAY = new InjectionToken('isSpinnerOverlay');
class SiLoadingSpinnerComponent {
    fadeAnimation = '';
    isBlockingSpinner = inject(LOADING_SPINNER_BLOCKING, { optional: true });
    isSpinnerOverlay = inject(LOADING_SPINNER_OVERLAY, { optional: true });
    /**
     * Needed for a11y
     */
    ariaLabel = $localize `:@@SI_LOADING_SPINNER.LABEL:Loading`;
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiLoadingSpinnerComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.1.5", type: SiLoadingSpinnerComponent, selector: "si-loading-spinner", inputs: { isBlockingSpinner: "isBlockingSpinner", isSpinnerOverlay: "isSpinnerOverlay", ariaLabel: "ariaLabel" }, host: { properties: { "@fadeAnimation": "this.fadeAnimation" } }, ngImport: i0, template: "<div\n  class=\"loading\"\n  aria-atomic=\"true\"\n  aria-live=\"assertive\"\n  [class.blocking-spinner]=\"isBlockingSpinner\"\n  [class.spinner-overlay]=\"isSpinnerOverlay\"\n>\n  <svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 512 512\">\n    <title>{{ ariaLabel | translate }}</title>\n    <g>\n      <path d=\"M256,156a16,16,0,0,1-16-16V80a16,16,0,0,1,32,0v60A16,16,0,0,1,256,156Z\" />\n      <path\n        d=\"M314.78,175.1a16,16,0,0,1-3.54-22.35l35.27-48.54A16,16,0,1,1,372.39,123l-35.26,48.54A16,16,0,0,1,314.78,175.1Z\"\n      />\n      <path\n        d=\"M351.11,225.1a16,16,0,0,1,10.27-20.16l57.06-18.54a16,16,0,1,1,9.89,30.43l-57.06,18.54A16,16,0,0,1,351.11,225.1Z\"\n      />\n      <path\n        d=\"M351.11,286.9a16,16,0,0,1,20.16-10.27l57.06,18.54a16,16,0,1,1-9.89,30.43l-57.06-18.54A16,16,0,0,1,351.11,286.9Z\"\n      />\n      <path\n        d=\"M314.78,336.9a16,16,0,0,1,22.35,3.54L372.39,389a16,16,0,1,1-25.88,18.81l-35.27-48.54A16,16,0,0,1,314.78,336.9Z\"\n      />\n      <path d=\"M256,356a16,16,0,0,1,16,16v60a16,16,0,0,1-32,0V372A16,16,0,0,1,256,356Z\" />\n      <path\n        d=\"M197.22,336.9a16,16,0,0,1,3.54,22.35l-35.27,48.54A16,16,0,1,1,139.61,389l35.26-48.54A16,16,0,0,1,197.22,336.9Z\"\n      />\n      <path\n        d=\"M160.89,286.9a16,16,0,0,1-10.27,20.16L93.56,325.6a16,16,0,0,1-9.89-30.43l57.06-18.54A16,16,0,0,1,160.89,286.9Z\"\n      />\n      <path\n        d=\"M160.89,225.1a16,16,0,0,1-20.16,10.27L83.67,216.83a16,16,0,1,1,9.89-30.43l57.06,18.54A16,16,0,0,1,160.89,225.1Z\"\n      />\n      <path\n        d=\"M197.22,175.1a16,16,0,0,1-22.35-3.54L139.61,123a16,16,0,1,1,25.88-18.81l35.27,48.54A16,16,0,0,1,197.22,175.1Z\"\n      />\n    </g>\n  </svg>\n</div>\n", styles: ["@keyframes spinner-fade{0%{opacity:1}to{opacity:.05}}.loading{display:block;text-align:center}.blocking-spinner{background:var(--element-base-translucent-1)}.spinner-overlay{position:absolute;inset:0;z-index:1090;display:flex;justify-content:center;align-items:center}svg{inline-size:var(--loading-spinner-size, 64px);block-size:var(--loading-spinner-size, 64px)}path{fill:var(--loading-spinner-color, var(--element-ui-2));animation:spinner-fade calc(1s * var(--element-animations-enabled, 1)) infinite linear}path:nth-child(1){animation-delay:0s;opacity:1}path:nth-child(2){animation-delay:.1s;opacity:.9}path:nth-child(3){animation-delay:.2s;opacity:.8}path:nth-child(4){animation-delay:.3s;opacity:.7}path:nth-child(5){animation-delay:.4s;opacity:.6}path:nth-child(6){animation-delay:.5s;opacity:.5}path:nth-child(7){animation-delay:.6s;opacity:.4}path:nth-child(8){animation-delay:.7s;opacity:.3}path:nth-child(9){animation-delay:.8s;opacity:.2}path:nth-child(10){animation-delay:.9s;opacity:.1}\n"], dependencies: [{ kind: "pipe", type: i1.SiTranslatePipe, name: "translate" }], animations: [
            trigger('fadeAnimation', [
                transition(':enter', [style({ opacity: 0 }), animate('200ms ease-in')]),
                transition(':leave', animate('200ms ease-out', style({ opacity: 0 })))
            ])
        ], changeDetection: i0.ChangeDetectionStrategy.OnPush });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiLoadingSpinnerComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-loading-spinner', changeDetection: ChangeDetectionStrategy.OnPush, animations: [
                        trigger('fadeAnimation', [
                            transition(':enter', [style({ opacity: 0 }), animate('200ms ease-in')]),
                            transition(':leave', animate('200ms ease-out', style({ opacity: 0 })))
                        ])
                    ], template: "<div\n  class=\"loading\"\n  aria-atomic=\"true\"\n  aria-live=\"assertive\"\n  [class.blocking-spinner]=\"isBlockingSpinner\"\n  [class.spinner-overlay]=\"isSpinnerOverlay\"\n>\n  <svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 512 512\">\n    <title>{{ ariaLabel | translate }}</title>\n    <g>\n      <path d=\"M256,156a16,16,0,0,1-16-16V80a16,16,0,0,1,32,0v60A16,16,0,0,1,256,156Z\" />\n      <path\n        d=\"M314.78,175.1a16,16,0,0,1-3.54-22.35l35.27-48.54A16,16,0,1,1,372.39,123l-35.26,48.54A16,16,0,0,1,314.78,175.1Z\"\n      />\n      <path\n        d=\"M351.11,225.1a16,16,0,0,1,10.27-20.16l57.06-18.54a16,16,0,1,1,9.89,30.43l-57.06,18.54A16,16,0,0,1,351.11,225.1Z\"\n      />\n      <path\n        d=\"M351.11,286.9a16,16,0,0,1,20.16-10.27l57.06,18.54a16,16,0,1,1-9.89,30.43l-57.06-18.54A16,16,0,0,1,351.11,286.9Z\"\n      />\n      <path\n        d=\"M314.78,336.9a16,16,0,0,1,22.35,3.54L372.39,389a16,16,0,1,1-25.88,18.81l-35.27-48.54A16,16,0,0,1,314.78,336.9Z\"\n      />\n      <path d=\"M256,356a16,16,0,0,1,16,16v60a16,16,0,0,1-32,0V372A16,16,0,0,1,256,356Z\" />\n      <path\n        d=\"M197.22,336.9a16,16,0,0,1,3.54,22.35l-35.27,48.54A16,16,0,1,1,139.61,389l35.26-48.54A16,16,0,0,1,197.22,336.9Z\"\n      />\n      <path\n        d=\"M160.89,286.9a16,16,0,0,1-10.27,20.16L93.56,325.6a16,16,0,0,1-9.89-30.43l57.06-18.54A16,16,0,0,1,160.89,286.9Z\"\n      />\n      <path\n        d=\"M160.89,225.1a16,16,0,0,1-20.16,10.27L83.67,216.83a16,16,0,1,1,9.89-30.43l57.06,18.54A16,16,0,0,1,160.89,225.1Z\"\n      />\n      <path\n        d=\"M197.22,175.1a16,16,0,0,1-22.35-3.54L139.61,123a16,16,0,1,1,25.88-18.81l35.27,48.54A16,16,0,0,1,197.22,175.1Z\"\n      />\n    </g>\n  </svg>\n</div>\n", styles: ["@keyframes spinner-fade{0%{opacity:1}to{opacity:.05}}.loading{display:block;text-align:center}.blocking-spinner{background:var(--element-base-translucent-1)}.spinner-overlay{position:absolute;inset:0;z-index:1090;display:flex;justify-content:center;align-items:center}svg{inline-size:var(--loading-spinner-size, 64px);block-size:var(--loading-spinner-size, 64px)}path{fill:var(--loading-spinner-color, var(--element-ui-2));animation:spinner-fade calc(1s * var(--element-animations-enabled, 1)) infinite linear}path:nth-child(1){animation-delay:0s;opacity:1}path:nth-child(2){animation-delay:.1s;opacity:.9}path:nth-child(3){animation-delay:.2s;opacity:.8}path:nth-child(4){animation-delay:.3s;opacity:.7}path:nth-child(5){animation-delay:.4s;opacity:.6}path:nth-child(6){animation-delay:.5s;opacity:.5}path:nth-child(7){animation-delay:.6s;opacity:.4}path:nth-child(8){animation-delay:.7s;opacity:.3}path:nth-child(9){animation-delay:.8s;opacity:.2}path:nth-child(10){animation-delay:.9s;opacity:.1}\n"] }]
        }], propDecorators: { fadeAnimation: [{
                type: HostBinding,
                args: ['@fadeAnimation']
            }], isBlockingSpinner: [{
                type: Input
            }], isSpinnerOverlay: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }] } });

class SiLoadingSpinnerDirective {
    /**
     * Displays the loading spinner when the value is either true or non-zero.
     */
    siLoading;
    /**
     * Displays semi-transparent backdrop for the spinner, default is false.
     */
    blocking = false;
    /**
     * Specifies if the spinner should be displayed after a delay, default is true.
     */
    initialDelay = true;
    el = inject(ElementRef);
    componentFactoryResolver = inject(ComponentFactoryResolver);
    appRef = inject(ApplicationRef);
    sub;
    progressSubject = new BehaviorSubject(false);
    off$ = this.progressSubject.pipe(filter(val => !val));
    on$ = this.progressSubject.pipe(filter(val => val));
    initialWaitTime = 500;
    minSpinTime = 500;
    compPortal = new ComponentPortal(SiLoadingSpinnerComponent);
    // this makes sure the spinner only displays with a delay of 500ms and stays for 500ms so
    // that it doesn't flicker
    spinner$ = this.on$.pipe(switchMap(() => merge(timer(this.initialWaitTime).pipe(map(() => true), takeUntil(this.off$)), combineLatest([this.off$, timer(this.initialWaitTime + this.minSpinTime)]).pipe(map(() => false)))));
    createPortal() {
        const providers = [
            { provide: LOADING_SPINNER_BLOCKING, useValue: this.blocking },
            {
                provide: LOADING_SPINNER_OVERLAY,
                useValue: true
            }
        ];
        const outlet = new DomPortalOutlet(this.el.nativeElement, this.componentFactoryResolver, this.appRef, Injector.create({ providers }));
        this.compPortal.attach(outlet);
    }
    ngOnInit() {
        this.sub = this.spinner$.subscribe(val => {
            if (val) {
                if (!this.compPortal.isAttached) {
                    this.createPortal();
                }
            }
            else if (this.compPortal.isAttached) {
                this.compPortal.detach();
            }
        });
    }
    ngOnChanges() {
        this.initialWaitTime = this.initialDelay ? 500 : 0;
        const newState = !!this.siLoading;
        if (newState !== this.progressSubject.value) {
            this.progressSubject.next(newState);
        }
    }
    ngOnDestroy() {
        this.sub?.unsubscribe();
        if (this.compPortal.isAttached) {
            this.compPortal.detach();
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiLoadingSpinnerDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "16.1.5", type: SiLoadingSpinnerDirective, selector: "[siLoading]", inputs: { siLoading: "siLoading", blocking: "blocking", initialDelay: "initialDelay" }, host: { classAttribute: "position-relative" }, usesOnChanges: true, ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiLoadingSpinnerDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[siLoading]',
                    host: {
                        class: 'position-relative'
                    }
                }]
        }], propDecorators: { siLoading: [{
                type: Input,
                args: [{ required: true }]
            }], blocking: [{
                type: Input
            }], initialDelay: [{
                type: Input
            }] } });

class SiLoadingService {
    /**
     * Counts the number of loads active, is `0` when all loading is finished (or hasn't started).
     */
    counter = new BehaviorSubject(0);
    /**
     * Start the loading.
     */
    startLoad() {
        this.counter.next(this.counter.value + 1);
    }
    /**
     * Stop the loading.
     */
    stopLoad() {
        if (this.counter.value > 0) {
            this.counter.next(this.counter.value - 1);
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiLoadingService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiLoadingService, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiLoadingService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }] });

class SiLoadingSpinnerModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiLoadingSpinnerModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.1.5", ngImport: i0, type: SiLoadingSpinnerModule, declarations: [SiLoadingSpinnerComponent, SiLoadingSpinnerDirective], imports: [CommonModule, SiTranslateModule], exports: [SiLoadingSpinnerComponent, SiLoadingSpinnerDirective] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiLoadingSpinnerModule, imports: [CommonModule, SiTranslateModule] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiLoadingSpinnerModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, SiTranslateModule],
                    declarations: [SiLoadingSpinnerComponent, SiLoadingSpinnerDirective],
                    exports: [SiLoadingSpinnerComponent, SiLoadingSpinnerDirective]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { LOADING_SPINNER_BLOCKING, LOADING_SPINNER_OVERLAY, SiLoadingService, SiLoadingSpinnerComponent, SiLoadingSpinnerDirective, SiLoadingSpinnerModule };
